/**
 * Cohesive ChatKit — ComposerShell + SuggestionPills.
 *
 * ComposerShell is the composer frame only (attachment chips → editor →
 * toolbar). The product's Lexical editor and its controls render into the
 * slots; the kit's plain Textarea was a demo stand-in, not an engine spec.
 *
 * Ported from cohesive-design-system/showcase/lib/chat-kit.jsx.
 */
import * as React from "react";

import { cn } from "@/lib/utils";
import { AttachmentChip, type ChatAttachment } from "./atoms";

/**
 * The composer frame: 1.5px border, rust focus ring (3px tinted glow),
 * rounded-xl. Owns layout only — state and behavior belong to the host.
 */
export function ComposerShell({
  attachments,
  onRemoveAttachment,
  attachmentsRow,
  toolbar,
  children,
  className,
}: {
  /** Attachment chip data; rendered above the editor when present. */
  attachments?: ChatAttachment[];
  onRemoveAttachment?: (attachment: ChatAttachment) => void;
  /** Fully custom attachments row (overrides `attachments`). */
  attachmentsRow?: React.ReactNode;
  /** Bottom controls row (attach · skills · @ · effort · mic · send). */
  toolbar?: React.ReactNode;
  /** The editor area. */
  children: React.ReactNode;
  className?: string;
}) {
  const chips =
    attachmentsRow ??
    (attachments && attachments.length > 0 ? (
      <div className="flex flex-wrap gap-1.5 px-1 pt-0.5">
        {attachments.map((a, i) => (
          <AttachmentChip
            // ChatAttachment has no id; two same-named files would collide on
            // key={a.name}, so disambiguate with the index.
            key={`${a.name}-${i}`}
            attachment={a}
            onRemove={
              onRemoveAttachment ? () => onRemoveAttachment(a) : undefined
            }
          />
        ))}
      </div>
    ) : null);

  return (
    <div
      className={cn(
        // Hover is scoped to :not(:focus-within) so the focus (rust) border
        // always wins when the composer is both focused and hovered — without
        // it, `hover:border-n-30` is emitted after `focus-within:border-rust`
        // and overrides the rust highlight on hover, dimming it backwards.
        "flex flex-col gap-2 rounded-xl border-2 border-n-20 bg-card px-2 pb-1.5 pt-3 transition-[border-color,box-shadow] duration-150 ease-out [&:hover:not(:focus-within)]:border-n-30 focus-within:border-rust focus-within:shadow-[0_0_0_3px_color-mix(in_oklab,var(--rust)_14%,transparent)]",
        className,
      )}
    >
      {chips}
      <div className="px-1.5 pb-0.5 pt-1">{children}</div>
      {toolbar && <div className="flex items-center gap-1">{toolbar}</div>}
    </div>
  );
}

export interface SuggestionPill {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}

/** Empty-state / follow-up suggestion pills above the composer. */
export function SuggestionPills({
  suggestions,
  onSelect,
  className,
}: {
  suggestions: SuggestionPill[];
  onSelect?: (suggestion: SuggestionPill) => void;
  className?: string;
}) {
  if (suggestions.length === 0) return null;
  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {suggestions.map((s) => {
        const Icon = s.icon;
        return (
          <button
            key={s.label}
            type="button"
            onClick={() => onSelect?.(s)}
            className="inline-flex min-h-7 items-center gap-2 rounded-full bg-card px-3 font-sans text-xs text-n-70 shadow-hairline outline-none transition-[box-shadow,color] duration-150 ease-out hover:text-n-90 hover:shadow-[0_0_0_1px_var(--n-30)] focus-visible:outline-2 focus-visible:outline-rust"
          >
            {Icon && <Icon className="size-3.5 text-n-40" />}
            {s.label}
          </button>
        );
      })}
    </div>
  );
}
